/**
 * W1-11b (app-shell-ui), revised by W3-01 (UX-010): the shortcut sheet (`?`; DESIGN.md §4.19, §8.4): every binding
 * of the active preset, grouped by task (the command categories: Playback, Edit, Notes, Motion, ...) in two columns,
 * then "Mouse and trackpad" (the wheel and drag gestures of the preset, e.g. Ctrl+wheel fine-tuning in the RPE
 * preset). Each row says where it works ("in the note lane, notes selected") under its title, and rows that work
 * right now are marked "Works now". Arguments read as words ("Move later: 4 steps", "Set beat snap: 1/12"; raw
 * argument names never show), digit families collapse into one row ("Segment curve by number" with 1-9), and
 * numbered rows sort by their number. Searchable by command, alias, situation or key (the mouse rows by "wheel"),
 * and printable: while the sheet is open, printing prints only the sheet (every group shown, in two columns, in the
 * light theme so it reads on paper; the dialog's scrolling frame is not printed). "Change shortcuts" opens the
 * keymap editor (`keymap.open`, W2-13) when it exists.
 */

import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Json } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Kbd } from '../../ui/Kbd';
import type { AppServices, CommandDef, KeyBinding, WhenContext } from '../registry/types';
import { parseWhenAst, REGION_ATOM_PREFIXES, type WhenAst } from '../registry/when';
import type { ShellUiService, UiSnapshot } from '../uiService';
import { argsKey, whenHolds } from './commandState';
import { normalizeText, searchScore } from './paletteSearch';
import { selectKey } from './selectKey';
import { snapLabel } from './SnapControls';
import { dependsOn, useKeymapVersion, useShell, useText, useUi, type ShellText } from './ShellContext';
import { m } from './shell.messages';
import p from './Palette.module.css';

const selectOpen = (snap: UiSnapshot): boolean => snap.cheatSheet;

/** Attribute on <html> while the sheet is open: print styles then print only the sheet. */
export const PRINT_SHEET_ATTR = 'data-printing-sheet';

export interface SheetRow {
  readonly key: string;
  /** The command (null for a mouse gesture row). */
  readonly command: CommandDef | null;
  readonly args: Json | undefined;
  readonly title: string;
  readonly chords: readonly string[];
  readonly activeNow: boolean;
  /** Where the binding works ("in the note lane, notes selected"); null: everywhere. */
  readonly situation: string | null;
}

export interface SheetGroup {
  readonly key: string;
  readonly title: string;
  readonly rows: readonly SheetRow[];
  readonly activeNow: boolean;
}

/**
 * Display name of a when-clause comparison value (W3-09 CR-5): the translated name of the known values (workspace,
 * level, preset, os, tool), else the raw one. Shared with the keymap editor's situation texts.
 */
export function whenValueLabel(name: string, value: string, text: ShellText): string {
  const key = WHEN_VALUE_KEYS[name]?.[value];
  return key === undefined ? value : text.t(key);
}

/** Message keys of the comparison values `describeWhen` names (other values print raw). */
const WHEN_VALUE_KEYS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  workspace: {
    chart: m.wsChart,
    timing: m.wsTiming,
    publish: m.wsPublish,
    library: m.wsLibrary,
    settings: m.wsSettings,
  },
  level: { guided: m.levelGuided, full: m.levelFull },
  preset: { default: 'settings.presetDefault', rpe: 'settings.presetRpe' },
  os: { mac: 'settings.ctxOsMac', other: 'settings.ctxOsOther' },
  tool: {
    select: 'noteLane.toolSelect',
    tap: 'noteLane.toolTap',
    drag: 'noteLane.toolDrag',
    flick: 'noteLane.toolFlick',
    hold: 'noteLane.toolHold',
    eraser: 'noteLane.toolEraser',
  },
};

/** Human description of a binding condition ("in the note lane, notes selected"); null = everywhere. */
export function describeWhen(ast: WhenAst, text: ShellText): string | null {
  const one = (a: WhenAst): string | null => {
    switch (a.t) {
      case 'true':
        return null;
      case 'atom': {
        const prefix = REGION_ATOM_PREFIXES.find((x) => a.name.startsWith(x));
        if (prefix !== undefined) {
          return text.t(m.atomRegion, {
            prefix: prefix.slice(0, -1),
            region: selectKey(a.name.slice(prefix.length)),
          });
        }
        return text.t(m.atom, { atom: selectKey(a.name), name: a.name });
      }
      case 'cmp':
        return text.t(m.atomCompare, {
          name: a.name,
          op: a.op === '!=' ? 'ne' : 'eq',
          value: whenValueLabel(a.name, a.value, text),
        });
      case 'not': {
        // "!textInput" and "!dialog" are implied by the keyboard router: not worth a word.
        if (a.e.t === 'atom' && (a.e.name === 'textInput' || a.e.name === 'dialog' || a.e.name === 'paletteOpen')) {
          return null;
        }
        // Selection kinds have their own words ("no notes selected"), never "not notes selected" (UX-010).
        if (a.e.t === 'atom' && a.e.name.startsWith('sel.')) {
          return text.t(m.cheatNone, { atom: selectKey(a.e.name), name: a.e.name });
        }
        const inner = one(a.e);
        return inner === null ? null : text.t(m.cheatNot, { what: inner });
      }
      case 'and': {
        const parts = a.items.map(one).filter((x): x is string => x !== null);
        return parts.length === 0 ? null : parts.reduce((acc, x) => text.t(m.cheatJoin, { a: acc, b: x }));
      }
      case 'or': {
        const parts = a.items.map(one).filter((x): x is string => x !== null);
        return parts.length === 0 ? null : parts.reduce((a, b) => text.t(m.orJoin, { a, b }));
      }
    }
  };
  return one(ast);
}

/** Argument object of a binding as key / value pairs (a bare value is `{ '': value }`). */
function argEntries(args: Json): ReadonlyArray<readonly [string, Json]> {
  return args !== null && typeof args === 'object' && !Array.isArray(args)
    ? Object.entries(args as { readonly [k: string]: Json })
    : [['', args]];
}

const numberIn = (entries: ReadonlyArray<readonly [string, Json]>, name: string): number | null => {
  const hit = entries.find(([k, v]) => (k === name || k === '') && typeof v === 'number');
  return hit === undefined ? null : (hit[1] as number);
};

/**
 * Words for the arguments of the shell's own parameterised bindings: the snap divisor as "1/12", the key jumps
 * across every animated property, a seek distance in seconds. Null when the command is not one of these.
 */
export function shellArgValue(cmd: CommandDef, args: Json, text: ShellText): string | null {
  const entries = argEntries(args);
  switch (cmd.id) {
    case 'snap.setDiv': {
      const div = numberIn(entries, 'div');
      return div === null ? null : snapLabel(div, text);
    }
    case 'nav.prevKey':
    case 'nav.nextKey':
      return entries.some(([k, v]) => k === 'any' && v === true) ? text.t(m.argAnyProperty) : null;
    case 'playback.seekSeconds': {
      const sec = numberIn(entries, 'sec');
      return sec === null ? null : text.t(m.argSecondsValue, { sec });
    }
    case 'playback.setRate': {
      const rate = numberIn(entries, 'rate');
      return rate === null ? null : text.t(m.rateOption, { rate });
    }
    default:
      return null;
  }
}

/**
 * Words for argument fields many commands share (UX-010): step counts, milliseconds, seconds, the quick export.
 * Fields without words are left out (their key already tells them apart); null when nothing is said.
 */
export function commonArgWords(args: Json, text: ShellText): string | null {
  const words: string[] = [];
  for (const [k, v] of argEntries(args)) {
    if (typeof v === 'number' && (k === 'steps' || k === 'times')) words.push(text.t(m.argSteps, { n: v }));
    else if (typeof v === 'number' && k === 'ms') words.push(text.t(m.argMs, { n: v }));
    else if (typeof v === 'number' && k === 'sec') words.push(text.t(m.argSecondsValue, { sec: v }));
    else if (k === 'quick' && v === true) words.push(text.t(m.argQuick));
    else if (k === 'any' && v === true) words.push(text.t(m.argAnyProperty));
  }
  return words.length === 0 ? null : words.reduce((a, b) => text.t(m.listJoin, { a, b }));
}

/**
 * Title of a binding's command with what its arguments pick: the option's label for a choice ("Switch workspace
 * (Chart)"), the shell's own argument words ("Set beat snap: 1/12", "Next keyframe: any animated property"), the
 * shared field words ("Move later: 4 steps"), else the plain title (raw argument names never show). A named choice
 * reads in parentheses so a title that ends in the field's own word ("Switch workspace") never prints "workspace:"
 * as if it were a raw argument (UX-010); value words the title already says are not repeated ("... by 1 ms").
 */
export function bindingTitle(cmd: CommandDef, args: Json | undefined, text: ShellText): string {
  const base = text.t(cmd.title);
  if (args === undefined) return base;
  const withValue = (value: string): string => text.t(m.labelValue, { label: base, value });
  const known = shellArgValue(cmd, args, text);
  if (known !== null) return withValue(known);
  const entries = argEntries(args);
  const spec = cmd.args;
  if (spec?.kind === 'choice') {
    const option = spec.options.find(
      (o) => argsKey(args) === argsKey(o.value) || entries.some(([, v]) => argsKey(v) === argsKey(o.value)),
    );
    if (option !== undefined) return text.t(m.argParen, { label: base, value: text.tr(option.label) });
  }
  const common = commonArgWords(args, text);
  if (common !== null) return base.includes(common) ? base : withValue(common);
  return base;
}

/** The number a row of a digit family stands for (`{ digit: 3 }`, `3`), or null. */
function familyNumber(args: Json | undefined): number | null {
  if (typeof args === 'number') return args;
  if (args === null || typeof args !== 'object' || Array.isArray(args)) return null;
  const entries = Object.entries(args as { readonly [k: string]: Json });
  if (entries.length !== 1) return null;
  const [k, v] = entries[0] ?? ['', null];
  return (k === 'digit' || k === 'index' || k === 'n') && typeof v === 'number' ? v : null;
}

/** Order of the task groups (command categories); the mouse gestures come last. */
const CATEGORY_ORDER: readonly string[] = [
  'Playback',
  'Edit',
  'Notes',
  'Motion',
  'Lines',
  'Select',
  'Tools',
  'Timing',
  'View',
  'File',
  'Publish',
  'Help',
];

/** Key of the mouse and trackpad group. */
export const MOUSE_GROUP = 'mouse';

interface GestureRow {
  readonly chord: string;
  readonly label: MessageKey;
  /** Only in this keymap preset. */
  readonly preset?: 'default' | 'rpe';
}

/** The timeline's wheel and drag gestures (render/timeline TimelineHost, the lane's RPE fine-tuning). */
const GESTURES: readonly GestureRow[] = [
  { chord: 'Wheel', label: m.gestureWheel },
  { chord: 'Shift+Wheel', label: m.gestureShiftWheel },
  { chord: 'Ctrl+Wheel', label: m.gestureCtrlWheel, preset: 'default' },
  { chord: 'Ctrl+Wheel', label: m.gestureCtrlWheelRpe, preset: 'rpe' },
  { chord: 'Alt+Wheel', label: m.gestureAltWheel, preset: 'default' },
  { chord: 'Alt+Wheel', label: m.gestureAltWheelRpe, preset: 'rpe' },
  { chord: 'Ctrl+Shift+Wheel', label: m.gestureCtrlShiftWheel },
];

/** Rows of the mouse group for a keymap preset. */
export function gestureRows(preset: string, text: ShellText): SheetRow[] {
  const rows = GESTURES.filter((g) => g.preset === undefined || g.preset === preset).map(
    (g): SheetRow => ({
      key: `gesture:${g.chord}`,
      command: null,
      args: undefined,
      title: text.t(g.label),
      chords: [g.chord],
      activeNow: false,
      situation: null,
    }),
  );
  rows.push({
    key: 'gesture:middleDrag',
    command: null,
    args: undefined,
    title: text.t(m.gestureMiddleDrag),
    chords: [],
    activeNow: false,
    situation: null,
  });
  return rows;
}

/** Joins distinct situations ("in the note lane; in the motion columns"); null when one of them is everywhere. */
function joinSituations(list: readonly (string | null)[], text: ShellText): string | null {
  if (list.some((x) => x === null)) return null;
  const distinct = [...new Set(list as readonly string[])];
  return distinct.length === 0 ? null : distinct.reduce((a, b) => text.t(m.cheatSituations, { a, b }));
}

/**
 * Builds the sheet: one group per command category (task), in CATEGORY_ORDER, then the mouse gestures; one row per
 * command and arguments (its chords and situations merged); digit families collapsed; rows by title, numbers in
 * numeric order.
 */
export function buildSheet(services: AppServices, when: WhenContext, text: ShellText): SheetGroup[] {
  const atoms = services.registry.whenAtomNames();
  interface Acc {
    cmd: CommandDef;
    args: Json | undefined;
    chords: string[];
    situations: (string | null)[];
    active: boolean;
  }
  const groups = new Map<string, Map<string, Acc>>();
  for (const b of services.keymap.bindings()) {
    if (b.remove === true) continue;
    const cmd = services.registry.command(b.command);
    if (cmd === undefined) continue;
    const ast = b.when === undefined ? ({ t: 'true' } as const) : parseWhenAst(b.when, atoms);
    const situation = 'error' in ast ? null : describeWhen(ast, text);
    let g = groups.get(cmd.category);
    if (g === undefined) {
      g = new Map();
      groups.set(cmd.category, g);
    }
    // A digit family ("Segment curve by number" 1..9) is one row; other arguments make their own rows.
    const family = familyNumber(b.args) !== null;
    const rkey = family ? `${b.command}\u0000#` : `${b.command}\u0000${argsKey(b.args)}`;
    const active = whenHolds(b.when, when, atoms) && whenHolds(cmd.when, when, atoms) && resolvesTo(services, b, when);
    const row = g.get(rkey);
    if (row === undefined) {
      g.set(rkey, { cmd, args: family ? undefined : b.args, chords: [b.chord], situations: [situation], active });
    } else {
      if (!row.chords.includes(b.chord)) row.chords.push(b.chord);
      row.situations.push(situation);
      row.active ||= active;
    }
  }
  const collator = new Intl.Collator(undefined, { numeric: true });
  const out: SheetGroup[] = [];
  const ordered = [...groups.keys()].sort(
    (a, b) =>
      (CATEGORY_ORDER.indexOf(a) + 1 || 99) - (CATEGORY_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b),
  );
  for (const category of ordered) {
    const g = groups.get(category);
    if (g === undefined) continue;
    const rows: SheetRow[] = [...g.entries()]
      .map(([rkey, r]) => ({
        key: rkey,
        command: r.cmd,
        args: r.args,
        title: bindingTitle(r.cmd, r.args, text),
        chords: r.chords,
        activeNow: r.active,
        situation: joinSituations(r.situations, text),
      }))
      .sort((a, b) => collator.compare(a.title, b.title));
    out.push({
      key: category,
      title: text.t(m.category, { category }),
      rows,
      activeNow: rows.some((r) => r.activeNow),
    });
  }
  out.push({
    key: MOUSE_GROUP,
    title: text.t(m.cheatMouse),
    rows: gestureRows(services.keymap.preset, text),
    activeNow: false,
  });
  return out;
}

function resolvesTo(services: AppServices, b: KeyBinding, when: WhenContext): boolean {
  try {
    const r = services.keymap.resolve(b.chord, when);
    return r === null || (r.command === b.command && argsKey(r.args) === argsKey(b.args));
  } catch {
    return true;
  }
}

/** Every query word appears in the text (substring, no fuzzy match: situations are long sentences). */
function containsWords(query: string, textValue: string): boolean {
  const t = normalizeText(textValue);
  return query
    .trim()
    .split(/\s+/)
    .filter((w) => w !== '')
    .every((w) => t.includes(normalizeText(w)));
}

function filterSheet(groups: readonly SheetGroup[], query: string, services: AppServices): SheetGroup[] {
  if (query.trim() === '') return [...groups];
  const out: SheetGroup[] = [];
  for (const g of groups) {
    const rows = g.rows.filter(
      (r) =>
        searchScore(query, {
          title: r.title,
          ...(r.command?.aliases === undefined ? {} : { aliases: r.command.aliases }),
          category: g.title,
          ...(r.command === null ? {} : { id: r.command.id }),
          detail: r.chords.map((c) => services.keymap.label(c)).join(' '),
        }) !== null ||
        (r.situation !== null && containsWords(query, `${r.title} ${r.situation}`)),
    );
    if (rows.length > 0) out.push({ ...g, rows });
  }
  return out;
}

export function CheatSheet(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const open = useUi(ui, selectOpen);
  if (!open) return null;
  return (
    <CheatSheetDialog
      services={services}
      onClose={() => {
        ui.setCheatSheet(false);
      }}
    />
  );
}

/**
 * While the sheet is mounted, printing prints only its print copy: <html> carries PRINT_SHEET_ATTR, and the page
 * switches to the light theme for the print (dark text on paper), then back.
 */
function usePrintOnlySheet(): void {
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute(PRINT_SHEET_ATTR, '');
    let saved: { readonly theme: string | null } | null = null;
    const before = (): void => {
      if (saved !== null) return;
      saved = { theme: root.getAttribute('data-theme') };
      root.setAttribute('data-theme', 'light');
    };
    const after = (): void => {
      if (saved === null) return;
      if (saved.theme === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', saved.theme);
      saved = null;
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      after();
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      root.removeAttribute(PRINT_SHEET_ATTR);
    };
  }, []);
}

function SheetGroups(props: { readonly groups: readonly SheetGroup[]; readonly text: ShellText }) {
  const { groups, text } = props;
  return groups.map((g) => (
    <section key={g.key} className={p.sheetGroup} aria-label={g.title}>
      <h3 className={p.sheetHeading}>{g.title}</h3>
      <table className={p.sheetTable}>
        <tbody>
          {g.rows.map((r) => (
            <tr key={r.key} data-active={r.activeNow ? 'true' : undefined}>
              <td className={p.sheetTitle}>
                <span>{r.title}</span>
                {r.activeNow && <span className={p.activeTag}>{text.t(m.cheatActiveNow)}</span>}
                {r.situation !== null && <span className={p.sheetSituation}>{r.situation}</span>}
              </td>
              <td className={p.sheetKeys}>
                {r.chords.length === 0 ? (
                  <span className={p.sheetGesture}>{text.t(m.gestureMiddleDragKeys)}</span>
                ) : (
                  r.chords.map((c) => <Kbd key={c} chord={c} />)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  ));
}

function CheatSheetDialog(props: { readonly services: AppServices; readonly onClose: () => void }) {
  const { services } = props;
  const env = useShell();
  const text = useText(services);
  const keymapVersion = useKeymapVersion(services);
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  // The context is captured when the sheet opens (the editor behind it does not change while it is up).
  const [when] = useState<WhenContext>(() => env.when());
  const groups = useMemo(() => {
    dependsOn(keymapVersion);
    return buildSheet(services, when, text);
  }, [services, when, text, keymapVersion]);
  const shown = useMemo(() => filterSheet(groups, deferred, services), [groups, deferred, services]);
  const keymapEditor = services.registry.command('keymap.open') !== undefined;
  const preset = services.keymap.preset;
  const description = text.t(m.cheatPreset, { preset, altFree: services.keymap.altFree ? 'yes' : 'no' });
  usePrintOnlySheet();

  return (
    <>
      <Dialog
        open
        size="lg"
        title={text.t(m.cheatTitle)}
        description={description}
        onClose={props.onClose}
        footer={
          <>
            {keymapEditor && (
              <Button
                variant="ghost"
                icon="Settings2"
                onClick={() => {
                  props.onClose();
                  void services.registry.runAny('keymap.open', undefined, 'button');
                }}
              >
                {text.t(m.cheatEdit)}
              </Button>
            )}
            <Button
              variant="secondary"
              icon="Printer"
              onClick={() => {
                window.print();
              }}
            >
              {text.t(m.cheatPrint)}
            </Button>
          </>
        }
      >
        <div className={p.sheet}>
          <label className={p.sheetSearch}>
            <span className={p.srOnly}>{text.t(m.cheatSearch)}</span>
            <input
              type="search"
              value={query}
              placeholder={text.t(m.cheatSearch)}
              // Ready to search on open; also keeps Escape a single press (the close button's tooltip would take it).
              autoFocus
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
          </label>
          {shown.length === 0 && <p className={p.empty}>{text.t(m.cheatEmpty)}</p>}
          <div className={p.sheetColumns}>
            <SheetGroups groups={shown} text={text} />
          </div>
        </div>
      </Dialog>
      {createPortal(
        // The printed copy (hidden on screen; what is listed now, without the dialog frame and the search field).
        <div className={p.printSheet} aria-hidden="true" data-print-sheet="">
          <h1 className={p.printTitle}>{text.t(m.cheatTitle)}</h1>
          <p className={p.printDescription}>{description}</p>
          <div className={p.printGroups}>
            <SheetGroups groups={shown} text={text} />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
